import { getActivityShareAbsoluteUrl } from "./activityShareDeepLink";

export function buildWhatsAppSendUrl(text) {
  return `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
}

function normalizeShareTitle(title) {
  if (title == null || title === "") return "Ohne Titel";
  const t = String(title).replace(/\\n/g, " ").replace(/\s+/g, " ").trim();
  return t || "Ohne Titel";
}

function normalizeShareLocation(loc) {
  if (loc == null || loc === "") return "";
  const s = String(loc).replace(/\n/g, " ").replace(/\s+/g, " ").trim();
  if (!s) return "";
  return s.length > 120 ? `${s.slice(0, 119)}…` : s;
}

/** WhatsApp prefill for story-style cards (deep link: home column/event). */
export function buildStoryCardShareMessage({ title, url, location }) {
  const t = normalizeShareTitle(title);
  const where = normalizeShareLocation(location);
  const place = where ? `\n${where}` : "";
  return `Ich habe diese Idee auf Albis gefunden – vielleicht etwas für euch?\n\n«${t}»${place}\n${url}`;
}

/** WhatsApp prefill for standard event/termin cards (same deep link pattern, different framing). */
export function buildEventCardShareMessage({ title, url, location }) {
  const t = normalizeShareTitle(title);
  const where = normalizeShareLocation(location);
  const place = where ? `\n${where}` : "";
  return `Hier ein Termin bzw. eine Aktivität, die ich auf Albis gesehen habe:\n\n«${t}»${place}\n${url}`;
}

/** WhatsApp prefill for a news article permalink. */
export function buildNewsArticleShareMessage({ title, url }) {
  const t = normalizeShareTitle(title);
  return `Diesen Artikel auf Albis fand ich lesenswert:\n\n«${t}»\n${url}`;
}

/** WhatsApp prefill forwarding today's Morning Briefing with partner invite link. */
export function buildMorningBriefingShareMessage({ sections, url }) {
  const lines = (sections || [])
    .filter((s) => s && s.text)
    .map((s) => `${s.label}: ${s.text}`);
  return `Ich nutze Albis für unser Morgen-Briefing — magst du auch?\n\n${lines.join("\n")}\n\nJeden Morgen für unsere Familie: ${url}`;
}

/** Prefilled DE message for “ask parents” — deep link to Aktivität teilen (Eigenes ablegen oder andere Eltern). */
export function buildParentInvitePrefillMessage() {
  const url = getActivityShareAbsoluteUrl();
  return `Hi! Ich sammle bei Albis Ideen zum Mitmachen — für uns später oder zum Weitergeben. Magst du hier einen Link eintragen?\n${url}`;
}

/**
 * WhatsApp prefill after a KiTa calendar win — peer recommendation.
 * @param {{ url?: string, eventCount?: number, source?: "mail"|"capture"|null }} [args]
 */
export function buildKitaPeerShareMessage({ url, eventCount, source } = {}) {
  const n = Number(eventCount);
  const fromFlyer = source === "capture";
  const fromMail = source === "mail";
  let lead = "";
  if (Number.isFinite(n) && n > 0) {
    const countLabel = n === 1 ? "1 Frist" : `${n} Fristen`;
    if (fromFlyer) {
      lead = `Albis hat ${countLabel} aus einem Flyer erkannt — `;
    } else if (fromMail) {
      lead = `Albis hat ${countLabel} aus unserer KiTa-Mail erkannt — `;
    } else {
      lead = `Albis hat ${countLabel} erkannt — `;
    }
  }
  return `${lead}KiTa-Mails weiterleiten oder Flyer fotografieren, Termine und Fristen mit einem Tipp im Kalender. Kennt ihr das auch?\n${url}`;
}

/** WhatsApp prefill after a KiTa win — partner joins the household. */
export function buildKitaPartnerShareMessage({ url }) {
  return `KiTa-Termine und Fristen seht ihr beide — private Sachen bleiben bei dir.\n${url}`;
}

const EXTRACT_SUMMARY_MAX = 500;
const EXTRACT_EVENT_MAX = 5;

function truncateShareText(text, max) {
  const s = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return "";
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1).trimEnd()}…`;
}

/** Friendly de-CH date for WhatsApp bullets, or null when no usable date. */
export function formatKitaExtractShareDate(event) {
  const ymd = String(event?.date || "");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return null;
  const weekday = date.toLocaleDateString("de-CH", { weekday: "short" });
  const dayMonth = date.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
  });
  const time = event.all_day === false && event.time ? ` ${event.time}` : "";
  return `${weekday} ${dayMonth}${time}`;
}

function buildExtractEventBullet(event) {
  const title = normalizeShareTitle(event?.title);
  const when = formatKitaExtractShareDate(event);
  const child = String(event?.child || "")
    .replace(/\s+/g, " ")
    .trim();
  const parts = [title];
  if (when) parts.push(when);
  if (child) parts.push(child);
  return `• ${parts.join(" — ")}`;
}

function appendCalendarLinks(lines, calendarUrls, outlookUrls) {
  const google = (calendarUrls || []).filter(Boolean).slice(0, EXTRACT_EVENT_MAX);
  const outlook = (outlookUrls || []).filter(Boolean).slice(0, EXTRACT_EVENT_MAX);
  if (google.length === 0 && outlook.length === 0) return;
  lines.push("", "Zum Kalender:");
  if (google.length > 0 && outlook.length > 0) {
    lines.push("Google:", ...google, "Outlook:", ...outlook);
    return;
  }
  lines.push(...(google.length > 0 ? google : outlook));
}

/**
 * WhatsApp prefill handing off a KiTa extraction to a co-parent.
 * Kurzfassung leads when present; bullets only for upcoming attention items.
 * @param {{ summary?: string, events?: object[], calendarUrls?: string[], outlookUrls?: string[], url: string }} args
 */
export function buildKitaExtractPartnerShareMessage({
  summary,
  events,
  calendarUrls,
  outlookUrls,
  url,
}) {
  const list = Array.isArray(events) ? events.filter(Boolean) : [];
  const summaryText = truncateShareText(summary, EXTRACT_SUMMARY_MAX);
  const invite = String(url || "").includes("/einladung/");
  const softLink = invite
    ? `Damit die Termine auch bei dir in Albis landen: ${url}`
    : `Falls du Albis auch nutzen willst: ${url}`;

  if (list.length === 0) {
    const body = summaryText || "Kurzfassung aus einer KiTa-Mail.";
    return `KiTa — Kurz von Albis:\n\n${body}\n\n${softLink}`;
  }

  const shown = list.slice(0, EXTRACT_EVENT_MAX);
  const bullets = shown.map(buildExtractEventBullet);
  const rest = list.length - shown.length;
  if (rest > 0) {
    bullets.push(`… und ${rest} weitere — in Albis unter KiTa.`);
  }

  const lines = [
    summaryText ? "KiTa — Kurz von Albis:" : "KiTa — von Albis erkannt:",
  ];
  if (summaryText) {
    lines.push("", summaryText);
  }
  lines.push("", "Zum Mitnehmen:", ...bullets);

  appendCalendarLinks(lines, calendarUrls, outlookUrls);

  lines.push("", softLink);
  return lines.join("\n");
}

/**
 * WhatsApp prefill for co-planning one Termin ("Schau mal — sollen wir?").
 * @param {{ title?: string, date?: string, time?: string|null, all_day?: boolean, place?: string|null, calendarUrl?: string|null, outlookUrl?: string|null }} args
 */
export function buildTerminCoPlanShareMessage({
  title,
  date,
  time,
  all_day: allDay,
  place,
  calendarUrl,
  outlookUrl,
}) {
  const t = normalizeShareTitle(title);
  const whenParts = [];
  if (date) {
    const fakeEvent = { date, time: allDay === false ? time : null, all_day: allDay };
    const when = formatKitaExtractShareDate(fakeEvent);
    if (when) whenParts.push(when);
  }
  const where = normalizeShareLocation(place);
  const detail = [whenParts.join(" "), where].filter(Boolean).join(" · ");
  const lines = [
    "Schau mal — sollen wir?",
    "",
    `«${t}»`,
  ];
  if (detail) lines.push(detail);
  appendCalendarLinks(
    lines,
    calendarUrl ? [calendarUrl] : [],
    outlookUrl ? [outlookUrl] : [],
  );
  return lines.join("\n");
}
